// AiMultiTimelineSurface.jsx — stacked timeline surfaces for multi-type AI plans

import React, { useMemo, useState } from 'react';

// Utilities
import { normalizeSurfaceNode } from 'spaces/utils/normalizeSurfaceNodes';
import { getPluralLabel } from 'utilities/Languages';

// CSS
import './AiMultiTimelineSurface.css';

// Components
import AiCalendarTimelineProvider from 'spaces/components/AiCalendarTimelineProvider';
import ViewRenderer from 'views/ViewRenderer';

const AiTimelineGroup = React.memo(({
  analysisKey,
  getType,
  group,
  language,
  sharedView,
  theme,
}) => {
  // ----- STATE ----- //

  const [selectedKeys, setSelectedKeys] = useState(() => new Set());
  const typeDef = getType(group?.typeId);
  const typeKey = String(typeDef?.key || group?.typeId || '').trim();

  // ----- MEMOS ----- //

  const groupView = useMemo(() => ({
    ...sharedView,
    timelineField: String(group?.timelineField || '').trim(),
    key: `ephemeral_${analysisKey}_${group?.typeId || 'timeline'}`,
  }), [analysisKey, group?.timelineField, group?.typeId, sharedView]);

  const nodes = useMemo(() => (
    (Array.isArray(group?.nodes) ? group.nodes : [])
      .map((node) => normalizeSurfaceNode(node, getType(node?.type)))
      .filter(Boolean)
  ), [getType, group?.nodes]);

  const groupLabel = getPluralLabel(typeDef, typeKey, language)
    || typeDef?.labelPlural
    || typeDef?.label
    || 'Timeline';

  // ----- RENDER ----- //

  return (
    <section className="ai-surface-message__timeline-group">

      {/* TIMELINE GROUP TITLE */}
      <h4
        className="ai-surface-message__timeline-group-title"
      >
        {groupLabel}
      </h4>
      {nodes.length > 0 ? (
        <AiCalendarTimelineProvider view={groupView}>
          <ViewRenderer
            displayMode="full"
            enforceExplicitProperties
            language={language}
            nodes={nodes}
            selectedKeys={selectedKeys}
            setSelectedKeys={setSelectedKeys}
            tableShowCheckboxes={false}
            typeDef={typeDef}
            view={groupView}
          />
        </AiCalendarTimelineProvider>
      ) : (
        <div
          className="ai-surface-message__empty"
          style={{
            color: theme.foregroundMuted,
          }}
        >
          {`No matching ${String(groupLabel).toLowerCase()} for this timeline.`}
        </div>
      )}
    </section>
  );
});

AiTimelineGroup.displayName = 'AiTimelineGroup';

const AiMultiTimelineSurface = React.memo(({
  analysisKey,
  getType,
  language,
  plan,
  surface,
  theme,
}) => {
  // ----- MEMOS ----- //

  const sharedView = useMemo(() => ({
    ...(plan?.presentation?.view || {}),
    ...(plan?.query?.scheduleWindow ? { scheduleWindow: plan.query.scheduleWindow } : {}),
  }), [plan?.presentation?.view, plan?.query?.scheduleWindow]);
  const timelineGroups = Array.isArray(surface?.timelineGroups) ? surface.timelineGroups : [];

  if (!timelineGroups.length) {
    return (
      <div
        className="ai-surface-message__empty"
        style={{
          color: theme.foregroundMuted,
        }}
      >
        No timeline groups available.
      </div>
    );
  }

  // ----- RENDER ----- //

  return (
    <div className="ai-surface-message__timeline-stack">
      {timelineGroups.map((group) => (
        <AiTimelineGroup
          key={String(group?.typeId || group?.timelineField || 'timeline')}
          analysisKey={analysisKey}
          getType={getType}
          group={group}
          language={language}
          sharedView={sharedView}
          theme={theme}
        />
      ))}
    </div>
  );
});

// Dev Tools
AiMultiTimelineSurface.displayName = 'AiMultiTimelineSurface';

export default AiMultiTimelineSurface;
